import { useCallback, useState } from "react";
import { CreateGenericChunkedUploader, GenerateUploadInput } from "../Utilities/GenericChunkedUploader";
import { ExtendedFile } from "../Utilities/UploadHelpers";
import { addIconsToFiles } from "../Utilities/mappers";
import { utilities } from "../Utilities/file";
import { UploadIdCardPublicInput } from "../Schemas/UploadIdCardPublicInput";
import { UploadIdCardOutput } from "../Schemas/UploadIdCardOutput";

export type UseIdCardUploadOptions = {
    requestId: string;
    requestTypeId?: number;
    linkTokenHash: string;
    uploadService: (input: UploadIdCardPublicInput) => Promise<UploadIdCardOutput>;
    onUploadSuccess?: (result: UploadIdCardOutput) => void;
    onUploadError?: (error: string) => void;
    onUploadComplete?: () => void;
};

export type UseIdCardUploadReturn = {
    isUploading: boolean;
    uploadError: string | null;
    uploadFiles: (files: File[]) => Promise<void>;
    clearError: () => void;
};

/**
 * Centralized hook for ID card upload logic.
 * Handles chunked upload, validation, and error management.
 * Backend automatically categorizes front/back sides via AI.
 */
export const useIdCardUpload = (options: UseIdCardUploadOptions): UseIdCardUploadReturn => {
    const { requestId, requestTypeId, linkTokenHash, uploadService, onUploadSuccess, onUploadError, onUploadComplete } = options;
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    // Generate upload input for ID card upload endpoint
    const generateUploadInput = useCallback((
        file: Record<string, any>,
        byteArray: Uint8Array | number[],
        isEndOfFile: boolean
    ): UploadIdCardPublicInput => {
        return {
            userName: undefined, // Will be set by backend
            requestId: requestId,
            isUploadFinished: isEndOfFile,
            uploadSessionId: file.sessionId,
            fileToUpload: {
                id: file.id,
                fileId: file.id,
                fileName: file.name,
                fileExtension: file.extension,
                // GenericChunkedUploader passes Array (converted from Uint8Array on line 156)
                // Backend expects Uint8Array but accepts Array as well during serialization
                fileChunk: byteArray as any,
                size: file.size,
                taxonomyId: undefined, // Backend will detect via AI
            },
            linkTokenHash,
        };
    }, [requestId, linkTokenHash]);

    // Error handler for upload failures
    // Error handler for upload failures
    // Errors are already handled in uploadServiceWrapper, so we only need to stop uploading
    const uploadErrorHandler = useCallback((_file: Record<string, any>) => {
        setIsUploading(false);
        // Don't override error - it's already set in uploadServiceWrapper with proper validation message
    }, []);

    // File upload completed handler
    const fileUploadCompleted = useCallback((file: Record<string, any>) => {
        file.uploadCompleted = true;
    }, []);

    // Progress update handler (no-op for now)
    const fileUploadProgressUpdate = useCallback((_file: Record<string, any>) => {
        // Progress update handler - can be extended if needed
    }, []);

    // Upload service wrapper
    const uploadServiceWrapper = useCallback((input: any): Promise<Record<string, any>> => {
        return new Promise(function (resolve, reject) {
            uploadService(input as UploadIdCardPublicInput)
                .then(function (result: UploadIdCardOutput) {
                    // Check if validation failed - validationMessage indicates validation error
                    if (result.validationMessage && result.validationMessage.trim() !== '') {
                        setUploadError(result.validationMessage);
                        onUploadError?.(result.validationMessage);
                        reject(new Error(result.validationMessage));
                        return;
                    }

                    // Call success handler when upload is finished
                    if (input.isUploadFinished) {
                        onUploadSuccess?.(result);
                    }

                    // Success - return result with sessionId for chunked upload
                    resolve({
                        sessionId: result.sessionId,
                        isValid: result.isValid,
                        frontFileId: result.frontFileId,
                        backFileId: result.backFileId,
                    });
                })
                .catch(function (err: any) {
                    // Generic error handling - only for actual errors, not validation messages
                    const errorMessage = err.message || "Nastala chyba pri nahrávaní ID karty.";
                    setUploadError(errorMessage);
                    onUploadError?.(errorMessage);
                    reject(err);
                });
        });
    }, [uploadService, onUploadSuccess, onUploadError]);

    // Get uploader instance
    const getUploader = useCallback(() => {
        // Create a wrapper that matches the expected signature
        const wrappedGenerateInput = (
            file: Record<string, any>,
            byteArray: Uint8Array,
            isEndOfFile: boolean
        ): GenerateUploadInput => {
            const input = generateUploadInput(file, byteArray, isEndOfFile);
            // Return in the format expected by GenericChunkedUploader
            return {
                requestId: input.requestId,
                requestTypeId: requestTypeId || 0, // Default to 0 if not provided
                parameters: [],
                isUploadFinished: input.isUploadFinished ?? false,
                uploadSessionId: input.uploadSessionId,
                sendNotification: false,
                fileToUpload: {
                    ...input.fileToUpload,
                    id: input.fileToUpload.id,
                    taxonomy: {} as any, // Dummy taxonomy - not used by backend
                },
            } as GenerateUploadInput;
        };

        return CreateGenericChunkedUploader(
            wrappedGenerateInput,
            uploadServiceWrapper,
            uploadErrorHandler,
            3145728, // chunk size
            fileUploadProgressUpdate,
            fileUploadCompleted,
            1499000 // max pixel size
        );
    }, [generateUploadInput, uploadServiceWrapper, uploadErrorHandler, fileUploadProgressUpdate, fileUploadCompleted, requestTypeId]);

    // Main upload function
    const uploadFiles = useCallback(async (filesToUpload: File[]) => {
        if (isUploading || filesToUpload.length === 0) return;

        setIsUploading(true);
        setUploadError(null);

        let extendedFiles: ExtendedFile[] = [];

        try {
            // Convert File objects to ExtendedFile (no taxonomy assignment - backend handles it)
            extendedFiles = filesToUpload.map((file) => {
                const extendedFile = file as ExtendedFile;
                extendedFile.id = utilities.guid();
                extendedFile.extension = utilities.getFileExtension(file.name);
                extendedFile.uploadCompleted = false;
                extendedFile.requestId = requestId;
                extendedFile.requestTypeId = requestTypeId;
                // No taxonomy assignment - backend will detect via AI
                return extendedFile;
            });

            // Add icons to files
            addIconsToFiles(extendedFiles, false, false);

            // Upload files using the chunked uploader
            const uploader = getUploader();
            await uploader.UploadFileFunction(extendedFiles);

            // Upload completed successfully
            setIsUploading(false);
            onUploadComplete?.();
        } catch (error: any) {
            setIsUploading(false);
            // Extract error message from backend response, or use the already set uploadError state, or fallback to generic message
            const errorMessage = error?.response?.data?.validationMessage ||
                error?.response?.data?.errorMessage ||
                error?.message ||
                uploadError ||
                "Nastala chyba pri nahrávaní ID kariet.";
            // Update uploadError state if we got a new message from the error
            if (errorMessage && errorMessage !== uploadError) {
                setUploadError(errorMessage);
                onUploadError?.(errorMessage);
            }
        }
    }, [isUploading, requestId, requestTypeId, getUploader, onUploadComplete, onUploadError, uploadError]);

    const clearError = useCallback(() => {
        setUploadError(null);
    }, []);

    return {
        isUploading,
        uploadError,
        uploadFiles,
        clearError,
    };
};

